import { useState, useEffect, useMemo, useId } from "react";
import { Link } from "wouter";
import {
  Check,
  ArrowRight,
  Sparkles,
  Users,
  Building2,
  BadgeCheck,
  Info,
} from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  getPackageSeats,
  getPackagePositioning,
  packageFitsTeam,
  recommendPackageCode,
  TEAM_SIZE_PRESETS,
  effectiveMonthlyPrice,
  maxAnnualDiscountPercent,
  formatAnnualSaveBadge,
  EVERY_PLAN_INCLUDES,
} from "../lib/packageSeats";

interface SaasPackage {
  id: number;
  name: string;
  code: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  allowedModules: string[];
  features: string[];
  isPopular: boolean;
  isActive: boolean;
}

const FAQ = [
  {
    q: "Is pricing per user or per organization?",
    a: "Per organization. You pay one flat subscription for your company tenant — not a fee for each employee. Each plan includes a stated maximum number of users.",
  },
  {
    q: "What counts as a user?",
    a: "Anyone with an active organization login in your tenant counts toward the plan limit — including employees using My Portal / self-service, managers, HR, finance, and admins. The separate vendor portal uses vendor authentication and is not an organization user login.",
  },
  {
    q: "What happens if we exceed our user limit?",
    a: "Choose a higher plan that covers your team size, or contact us to adjust your subscription. Plans are selected by organization user capacity and included modules.",
  },
  {
    q: "Can we change or upgrade plans later?",
    a: "Yes. You can upgrade or adjust your subscription with our support team. Newly included modules unlock for your organization when the plan changes.",
  },
  {
    q: "Is monthly billing available?",
    a: "Yes. Use the Monthly / Annual toggle on this page. Annual billing charges the yearly amount upfront and shows the effective monthly equivalent.",
  },
  {
    q: "What does annual billing include?",
    a: "The same plan modules and user capacity as monthly billing, paid as one yearly amount per organization. The toggle shows the effective monthly price and the total billed yearly.",
  },
  {
    q: "Do Indian statutory payroll/compliance features come with the plan?",
    a: "Indian PF, ESI, Professional Tax, TDS, and GST tooling are available in plans that include the Payroll and/or Finance modules (Standard and above for Payroll; Business and above for full Finance). They are not billed separately per employee.",
  },
];

function formatInr(amount: number) {
  return amount.toLocaleString("en-IN");
}

function teamSizeLabel(n: number) {
  return n >= 1000 ? "1000+" : String(n);
}

export default function Pricing() {
  useSEO({
    title: "Pricing & SaaS Packages — Alea ERP",
    description:
      "Transparent per-organization ERP plans with clear user limits. One price for your company — no per-employee charges.",
    path: "/pricing",
  });

  const billingSwitchId = useId();
  const teamSliderId = useId();

  const [packages, setPackages] = useState<SaasPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(true);
  const [teamSize, setTeamSize] = useState(50);

  useEffect(() => {
    fetch("/api/public/packages")
      .then((res) => res.json())
      .then((data) => {
        setPackages(data.packages || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const recommendedCode = useMemo(
    () => recommendPackageCode(packages, teamSize),
    [packages, teamSize],
  );

  const recommendedPkg = packages.find(
    (p) => p.code.toUpperCase() === recommendedCode?.toUpperCase(),
  );

  const saveBadge = useMemo(() => {
    const pct = maxAnnualDiscountPercent(packages);
    return pct == null ? null : formatAnnualSaveBadge(pct);
  }, [packages]);

  const sliderIndex = useMemo(() => {
    const exact = TEAM_SIZE_PRESETS.indexOf(teamSize as (typeof TEAM_SIZE_PRESETS)[number]);
    if (exact >= 0) return exact;
    const idx = TEAM_SIZE_PRESETS.findIndex((n) => n >= teamSize);
    return idx < 0 ? TEAM_SIZE_PRESETS.length - 1 : idx;
  }, [teamSize]);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-16 lg:pt-40">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-400/30 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" aria-hidden="true" />
              <span>Simple, transparent ERP pricing</span>
            </div>
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              One price for your organization
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-300">
              No per-employee charges. Pick a plan by team size and the modules you need.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white">
                <Building2 className="h-3.5 w-3.5 text-blue-300" aria-hidden="true" />
                Per organization
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white">
                <Users className="h-3.5 w-3.5 text-emerald-300" aria-hidden="true" />
                Users included
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white">
                <BadgeCheck className="h-3.5 w-3.5 text-amber-300" aria-hidden="true" />
                No per-employee billing
              </span>
            </div>

            {/* Monthly / Annual toggle */}
            <div className="pt-8 flex items-center justify-center gap-3">
              <label
                htmlFor={billingSwitchId}
                className={`text-sm cursor-pointer ${!isYearly ? "text-white font-bold" : "text-slate-400 font-medium"}`}
              >
                Monthly
              </label>
              <Switch
                id={billingSwitchId}
                checked={isYearly}
                onCheckedChange={setIsYearly}
                aria-label={isYearly ? "Annual billing selected" : "Monthly billing selected"}
              />
              <label
                htmlFor={billingSwitchId}
                className={`text-sm cursor-pointer flex items-center gap-1.5 ${
                  isYearly ? "text-white font-bold" : "text-slate-400 font-medium"
                }`}
              >
                Annual
                {saveBadge && (
                  <span className="rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold px-2 py-0.5">
                    {saveBadge}
                  </span>
                )}
              </label>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Team-size selector */}
      <section className="relative z-10 -mt-8 px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl shadow-slate-200/60">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Find your plan
                </p>
                <h2 className="text-lg font-extrabold text-[#0a2d4a]" id={`${teamSliderId}-label`}>
                  How many people will use Alea ERP?
                </h2>
              </div>
              <div className="text-left sm:text-right">
                <div className="text-3xl font-extrabold text-[#0a2d4a] tabular-nums" aria-live="polite">
                  {teamSizeLabel(teamSize)}
                  <span className="ml-1.5 text-sm font-semibold text-slate-500">users</span>
                </div>
                {recommendedPkg && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Recommended:{" "}
                    <span className="font-bold text-blue-700">{recommendedPkg.name}</span>
                  </p>
                )}
              </div>
            </div>

            <Slider
              id={teamSliderId}
              value={[sliderIndex]}
              min={0}
              max={TEAM_SIZE_PRESETS.length - 1}
              step={1}
              onValueChange={(v) => setTeamSize(TEAM_SIZE_PRESETS[v[0] ?? 0])}
              className="w-full"
              aria-labelledby={`${teamSliderId}-label`}
              aria-valuetext={`${teamSizeLabel(teamSize)} users`}
            />

            <div className="mt-3 flex justify-between text-[10px] sm:text-xs font-medium text-slate-400 tabular-nums">
              {TEAM_SIZE_PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setTeamSize(n)}
                  aria-pressed={teamSize === n}
                  aria-label={`${teamSizeLabel(n)} users`}
                  className={`hover:text-blue-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm px-0.5 ${
                    teamSize === n ? "text-blue-700 font-bold underline underline-offset-2" : ""
                  }`}
                >
                  {teamSizeLabel(n)}
                </button>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-100 px-3.5 py-3 text-xs text-blue-900 leading-relaxed">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-600" aria-hidden="true" />
              <span>
                Prices are <strong>per organization / {isYearly ? "month (billed annually)" : "month"}</strong>
                . Users are included in each plan — there are <strong>no per-employee charges</strong>.
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Package cards */}
      <section className="bg-slate-50 pt-12 pb-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {loading ? (
            <div className="text-center py-16 text-slate-500">Loading active subscription plans...</div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 items-stretch">
              {packages.map((pkg, i) => {
                const seats = getPackageSeats(pkg.code);
                const positioning = getPackagePositioning(pkg.code);
                const fits = packageFitsTeam(pkg.code, teamSize);
                const isRecommended =
                  recommendedCode != null &&
                  pkg.code.toUpperCase() === recommendedCode.toUpperCase();
                const isStandard = pkg.code.toUpperCase() === "STANDARD";
                const isEnterprise = pkg.code.toUpperCase() === "ENTERPRISE";
                const price = effectiveMonthlyPrice(pkg.monthlyPrice, pkg.yearlyPrice, isYearly);
                const highlight = isRecommended;
                const description = positioning.description || pkg.description;
                const showMostPopular = isStandard && (pkg.isPopular || isRecommended);

                return (
                  <Reveal key={pkg.id} delay={i * 0.08}>
                    <div
                      className={`relative flex h-full flex-col justify-between rounded-2xl border p-6 bg-white transition-all duration-300 ${
                        highlight
                          ? "border-blue-600 shadow-xl ring-2 ring-blue-600/30 scale-[1.02]"
                          : fits
                            ? "border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md"
                            : "border-slate-200 shadow-sm opacity-60"
                      }`}
                      aria-current={highlight ? "true" : undefined}
                    >
                      {showMostPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white shadow-sm whitespace-nowrap">
                          Most Popular
                        </div>
                      )}

                      {highlight && !showMostPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-1 text-xs font-bold text-white shadow-sm whitespace-nowrap">
                          Recommended
                        </div>
                      )}

                      {!fits && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-slate-600 px-3 py-1 text-[10px] font-bold text-white shadow-sm whitespace-nowrap">
                          {seats.label}
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <h3 className="text-xl font-bold text-[#0a2d4a]">{pkg.name}</h3>
                          {positioning.tagline && (
                            <p className="mt-0.5 text-xs font-semibold text-blue-700">
                              {positioning.tagline}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-slate-500 min-h-[36px]">{description}</p>
                          {isEnterprise && positioning.valueLine && (
                            <p className="mt-2 text-[11px] font-bold text-[#0a2d4a] bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                              {positioning.valueLine}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-700 shrink-0">
                            <Users className="h-4 w-4" aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              Users included
                            </p>
                            <p className="text-sm font-extrabold text-[#0a2d4a] truncate">
                              {seats.label}
                            </p>
                          </div>
                        </div>

                        <div className="pt-1 border-t border-slate-100">
                          <div className="flex items-baseline gap-1 flex-wrap">
                            <span className="text-3xl font-extrabold text-[#0a2d4a] tabular-nums">
                              ₹{formatInr(price)}
                            </span>
                            <span className="text-xs text-slate-500 font-medium">
                              / org / mo
                            </span>
                          </div>
                          {isYearly ? (
                            <p className="text-[11px] text-emerald-600 mt-0.5 font-medium">
                              ₹{formatInr(pkg.yearlyPrice)} billed yearly per organization
                            </p>
                          ) : (
                            <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                              Billed monthly per organization
                            </p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-1">
                            No per-employee charges
                          </p>
                        </div>

                        <div className="space-y-2 pt-1">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                            Included Modules
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {(pkg.allowedModules || []).slice(0, isEnterprise ? 6 : 8).map((mod) => (
                              <Badge
                                key={mod}
                                className="text-[10px] uppercase font-mono font-bold bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5"
                              >
                                {mod}
                              </Badge>
                            ))}
                            {(pkg.allowedModules || []).length > (isEnterprise ? 6 : 8) && (
                              <Badge className="text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5">
                                +{(pkg.allowedModules || []).length - (isEnterprise ? 6 : 8)} more
                              </Badge>
                            )}
                          </div>
                        </div>

                        {pkg.features && pkg.features.length > 0 && (
                          <div className="space-y-2 pt-2 border-t border-slate-100">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Features
                            </p>
                            <ul className="space-y-2">
                              {pkg.features.map((f, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                                  <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="pt-6 mt-6 border-t border-slate-100 flex flex-col gap-2">
                        <Link
                          href={`/pricing/details?code=${pkg.code}`}
                          className="w-full text-center text-xs font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center justify-center gap-1 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-md"
                        >
                          <span>Know More & Specifications</span>
                          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </Link>

                        {fits ? (
                          <Link
                            href={`/sign-up?package=${pkg.code}`}
                            className={`w-full rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                              highlight || showMostPopular
                                ? "bg-blue-600 text-white hover:bg-blue-500 shadow-md"
                                : "border border-slate-300 text-[#0a2d4a] hover:bg-slate-50"
                            }`}
                          >
                            <span>Get Started</span>
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        ) : (
                          <span
                            className="w-full rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 border border-slate-200 text-slate-400"
                            aria-disabled="true"
                          >
                            Upgrade for more users
                          </span>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Every plan includes */}
      <section className="bg-slate-50 pb-16">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-7 sm:px-8">
              <h2 className="text-center text-lg font-extrabold text-[#0a2d4a]">
                Every plan includes
              </h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {EVERY_PLAN_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              Frequently Asked Questions
            </h2>
          </Reveal>
          <div className="mt-10 space-y-4">
            {FAQ.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.06}>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6">
                  <h3 className="text-base font-bold text-[#0a2d4a]">{item.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
